import { NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { MotifButtonModule, MotifFooterModule } from '@ey-xd/ng-motif';
import { TranslatePipe } from '@ngx-translate/core';
import { MainHelperService } from '@services';

@Component({
  selector: 'app-footer',
  standalone: true,
  imports: [MotifFooterModule, MotifButtonModule, NgOptimizedImage, TranslatePipe],
  templateUrl: './footer.component.html',
  styleUrl: './footer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FooterComponent implements OnInit {
  #helper = inject(MainHelperService);
  #destroyRef = inject(DestroyRef);
  #activatedRoute = inject(ActivatedRoute);

  public logoURL = signal<string>('');
  public currentYear = new Date().getFullYear();

  public readonly privacyNoticeLinkAvailable = signal<boolean>(true);

  #privacyNoticeShown = signal<boolean>(false);

  public showPrivacyPolicy(): void {
    if (this.#privacyNoticeShown()) return;

    this.#privacyNoticeShown.set(true);
    this.#helper
      .checkPrivacyNotice(true, 'show')
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.#privacyNoticeShown.set(false));
  }

  ngOnInit(): void {
    this.#updateLogoURL();
    this.#subscribeToThemeToggle();

    this.#activatedRoute.data.subscribe(data => {
      if (
        Object.prototype.hasOwnProperty.call(data, 'hidePrivacyNoticeLink') &&
        data['hidePrivacyNoticeLink'] === true
      ) {
        this.privacyNoticeLinkAvailable.set(false);
      }
    });
  }

  #updateLogoURL(): void {
    const logoPath = this.#helper.isDarkTheme()
      ? 'assets/images/eytc-logo-shape-dark.svg'
      : 'assets/images/eytc-logo-shape.svg';
    this.logoURL.set(logoPath);
  }

  #subscribeToThemeToggle(): void {
    this.#helper
      .subscribeToThemeToggle()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.#updateLogoURL());
  }
}
